import {
  reelArtDirectionSchema,
  reelDirectionCheckpoints,
  type ReelArtDirection,
} from "./lead-gen-reel-direction";
import {
  fitReelColor,
  reelTextBackdrop,
  reelContrast,
} from "./lead-gen-reel-legibility";
import { readableTextColor } from "./color";
import { z } from "zod/v4";

/** Limits include spaces/punctuation; reject overflow instead of truncating claims. */
export const REEL_COPY_LIMITS = {
  hook: 38,
  bridge: 38,
  message: 90,
  cta: 38,
  title: 50,
  bullet: 28,
} as const;
const line = (max: number) => z.string().trim().min(1).max(max);
export const REEL_EDITOR_VERSION = "reel-editor-v3";
export const reelCreativeDecisionSchema = z.object({
  version: z.literal(REEL_EDITOR_VERSION),
  approach: z.string().min(1).max(80),
  hypothesis: z.string().min(1).max(400),
  selectionReason: z.string().min(1).max(1200),
});
export const leadReelStorySchema = z.object({
  creative: reelCreativeDecisionSchema.optional(),
  artDirection: reelArtDirectionSchema.optional(),
  hook: line(REEL_COPY_LIMITS.hook).describe("Opening hook, at most 7 words"),
  bridge: line(REEL_COPY_LIMITS.bridge).describe(
    "Second beat, at most 7 words; adds a reason to keep watching",
  ),
  message: line(REEL_COPY_LIMITS.message).describe(
    "One supported benefit or proof point, at most 18 words",
  ),
  cta: line(REEL_COPY_LIMITS.cta).describe(
    "Request a confidential broker consultation; no valuation offer",
  ),
  title: line(REEL_COPY_LIMITS.title),
  bullets: z.array(line(REEL_COPY_LIMITS.bullet)).length(3),
});
export type LeadReelStory = z.infer<typeof leadReelStorySchema>;
export const REEL_DESIGN_VERSION = "lead-reel-v13";
// Conservative intersection for Stories/Reels: extra space above lower controls/disclaimers.
// Background ribbons bleed to x=1080; meaningful content stays within this rectangle.
export const REEL_SAFE_AREA = {
  left: 80,
  right: 1004,
  top: 280,
  bottom: 1140,
} as const;
export function leadReelReviewTimes(
  template: "photo-build" | "gradient-reveal" | "story-scenes",
  direction?: ReelArtDirection,
) {
  if (direction && template !== "photo-build")
    return reelDirectionCheckpoints(direction);
  return template !== "photo-build"
    ? [1, 3.7, 5.5, 7, 10.5, 13.5]
    : [0.5, 2.5, 5.3, 8, 10.5, 13.5];
}
const color = z
  .string()
  .regex(/^#[0-9a-f]{6}$/i)
  .nullable()
  .optional();
export const leadReelBrandSchema = z.object({
  background: color,
  highlight: color,
  cta: color,
  text: color,
  ctaText: color,
});
export type LeadReelBrandOverride = z.infer<typeof leadReelBrandSchema>;
export type LeadReelPalette = Record<keyof LeadReelBrandOverride, string>;
function mix(hex: string, target: number, amount: number): string {
  return `#${[1, 3, 5]
    .map((offset) =>
      Math.round(
        Number.parseInt(hex.slice(offset, offset + 2), 16) * (1 - amount) +
          target * amount,
      )
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}
export function resolveLeadReelPalette(
  accent: string,
  override?: LeadReelBrandOverride | null,
): LeadReelPalette {
  const valid = /^#[0-9a-f]{6}$/i.test(accent) ? accent : "#10a85a";
  const requestedText = override?.text ?? "#ffffff";
  const backdrop = reelTextBackdrop(requestedText);
  const text = fitReelColor(requestedText, backdrop);
  const requestedCta = override?.cta ?? valid;
  const requestedCtaText =
    override?.ctaText ?? readableTextColor(requestedCta).toLowerCase();
  const ctaText =
    reelContrast(requestedCta, requestedCtaText) >= 4.5
      ? requestedCtaText
      : fitReelColor(
          readableTextColor(requestedCta).toLowerCase(),
          requestedCta,
          4.5,
        );
  return {
    background: fitReelColor(override?.background ?? mix(valid, 0, 0.82), text),
    highlight: fitReelColor(
      override?.highlight ?? mix(valid, 255, 0.5),
      backdrop,
    ),
    cta: requestedCta,
    text,
    ctaText,
  };
}
